"use client";

import React, { useCallback, useMemo } from "react";
import { useUniversalAuth, ModalContainer, MODAL_IDS, ResponsiveModal } from "@west-tennessee-consulting/ui";
import { EnhancedGenericCreateForm } from "@/components/shared/EnhancedGenericCreateForm";
import { TASK_CONFIG, taskTemplateTagSets } from "@/features/tasks/components/TaskConfig";
import { TaskTemplateSelector } from "@/features/tasks/components/TaskTemplateSelector";
import { SaveTemplateModal } from "@/features/tasks/components/SaveTemplateModal";
import { TASK_STATUS_OPTIONS, TASK_PRIORITY_OPTIONS, CreateTaskFormSchema } from "@/lib/schemas/task";
import type { FieldOption } from "@/lib/types/table/field-config-subtypes";

// Static option lists — hoisted to module scope so the memo'd
// EnhancedGenericCreateForm gets stable array references across renders.
const TASK_STATUS_FIELD_OPTIONS = [...TASK_STATUS_OPTIONS] as FieldOption[];
const TASK_PRIORITY_FIELD_OPTIONS = [...TASK_PRIORITY_OPTIONS] as FieldOption[];
import type { Message } from "@/lib/core/crm/types";
import { useCreateTask } from "@/lib/hooks/api/tasks";
import { useContactByEmail } from "@/features/contacts/hooks";
import { toast } from "sonner";
import type { CreateTaskModalData } from "@/lib/modals/data/modal-data-types";
import {
  createTaskDataFromMessage,
  extractEmailForContactMatching,
} from "@/lib/utils/email-task-utils";


/**
 * Unified Task Creation Modal
 *
 * Consolidates all task creation scenarios:
 * - Direct task creation from quick actions
 * - Task creation from contact / ticket / deal (prefilled context)
 * - Task creation from email message (inbox, message detail, compose)
 *
 * Email flow: openModal(MODAL_IDS.tasks.create, { message })
 * Mounted once via GlobalQuickActionModals in the root layout.
 */
export function UnifiedTaskCreationModal() {
  const { appUser } = useUniversalAuth();
  const { mutateAsync: createTask, isPending } = useCreateTask();

  return (
    <ModalContainer<CreateTaskModalData> modalId={MODAL_IDS.tasks.create}>
      {({ isOpen, data, onClose }) => (
        <UnifiedTaskCreationModalContent
          data={data}
          isOpen={isOpen}
          onClose={onClose}
          appUser={appUser}
          createTask={createTask}
          isPending={isPending}
        />
      )}
    </ModalContainer>
  );
}

interface UnifiedTaskCreationModalContentProps {
  data?: CreateTaskModalData;
  isOpen: boolean;
  onClose: () => void;
  appUser: { id?: string } | null | undefined;
  createTask: ReturnType<typeof useCreateTask>["mutateAsync"];
  isPending: boolean;
}

function buildContextInitialValues(
  data: CreateTaskModalData | undefined,
  appUserId: string | undefined,
  effectiveContactId: string | undefined
): Record<string, unknown> {
  const values: Record<string, unknown> = {};

  if (appUserId) {
    values.user_id = appUserId;
  }

  const contactId = data?.contactId ?? effectiveContactId;
  if (contactId) {
    values.contact_id = contactId;
  }

  if (data?.companyId) {
    values.company_id = data.companyId;
  }

  if (data?.ticketId) {
    values.ticket_id = data.ticketId;
  }

  if (data?.entityType && data?.entityId) {
    values.entity_type = data.entityType;
    values.entity_id = data.entityId;
  } else if (data?.dealId) {
    values.entity_type = "deals";
    values.entity_id = data.dealId;
  }

  if (data?.prefillData) {
    Object.assign(values, data.prefillData);
  }

  return values;
}

function UnifiedTaskCreationModalContent({
  data,
  isOpen,
  onClose,
  appUser,
  createTask,
  isPending,
}: UnifiedTaskCreationModalContentProps) {
  const message = data?.message;
  const senderEmail = message ? extractEmailForContactMatching(message) : undefined;
  const shouldLookupContact = !data?.contactId && !!senderEmail;

  const { data: contactByEmail } = useContactByEmail(
    shouldLookupContact ? senderEmail : ""
  );

  const effectiveContactId = data?.contactId ?? contactByEmail?.id;

  const initialValues = useMemo(() => {
    const contextValues = buildContextInitialValues(data, appUser?.id, effectiveContactId);

    if (message) {
      const fromEmail = createTaskDataFromMessage(
        message,
        effectiveContactId,
        appUser?.id
      );
      return { ...fromEmail, ...contextValues };
    }

    return contextValues;
  }, [data, appUser?.id, message, effectiveContactId]);

  const formResetKey = useMemo(() => {
    if (!isOpen) return "closed";
    if (message?.id) {
      return `message-${message.id}-${effectiveContactId ?? "no-contact"}`;
    }
    return [
      data?.contactId,
      data?.ticketId,
      data?.companyId,
      data?.dealId,
      data?.entityType,
      data?.entityId,
    ]
      .filter(Boolean)
      .join("-") || "blank";
  }, [isOpen, message?.id, effectiveContactId, data]);

  const handleSubmit = useCallback(
    async (formData: Record<string, unknown>) => {
      try {
        const validatedTask = CreateTaskFormSchema.parse(formData);
        const createdTask = await createTask(validatedTask);
        toast.success("Task created successfully");
        // NO Slack call here — the `contact_tasks_created` database webhook
        // posts the card for every creator. See webhooks/tasks/created/route.ts.
        data?.onTaskCreated?.(createdTask);
        onClose();
      } catch (error) {
        console.error("Failed to create task:", error);
        toast.error("Failed to create task");
        throw error;
      }
    },
    [createTask, data, onClose]
  );

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const title = useMemo(() => {
    if (message) return "Create Task from Email";
    if (data?.ticketId) return "Create Task for Ticket";
    if (data?.contactId || effectiveContactId) return "Create Task for Contact";
    if (data?.dealId || data?.entityType === "deals") return "Create Task for Deal";
    return "Create Task";
  }, [message, data?.ticketId, data?.contactId, data?.dealId, data?.entityType, effectiveContactId]);

  const description = useMemo(() => {
    if (message) {
      return `Create a task based on the email from ${message.sender?.name ?? message.sender?.email ?? "sender"}`;
    }
    if (data?.ticketId) return "Create a new task linked to this ticket";
    if (data?.contactId || effectiveContactId) return "Create a new task for this contact";
    if (data?.dealId || data?.entityType === "deals") return "Create a new task linked to this deal";
    return "Create a new task";
  }, [message, data?.ticketId, data?.contactId, data?.dealId, data?.entityType, effectiveContactId]);

  return (
    <ResponsiveModal
      globalModalId={MODAL_IDS.tasks.create}
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      title={title}
      description={description}
      maxWidth="3xl"
    >
      <EnhancedGenericCreateForm
        key={formResetKey}
        entityName="tasks"
        config={TASK_CONFIG}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        initialValues={initialValues}
        isLoading={isPending}
        expandInlineVariant="floating"
        templateSelector={TaskTemplateSelector}
        saveTemplateModal={SaveTemplateModal}
        tagSets={taskTemplateTagSets}
        statusOptions={TASK_STATUS_FIELD_OPTIONS}
        priorityOptions={TASK_PRIORITY_FIELD_OPTIONS}
      />
    </ResponsiveModal>
  );
}

export default UnifiedTaskCreationModal;
